import { GEOFENCE_SOURCE } from '../utils/map-constants.js'
import { wktToGeoJSON } from '../utils/wkt-parser.js'

// Normaliza attrs.mandounico a una de las 4 categorias del filtro.
// Legacy guarda 'geofence' o null/undefined para las regulares, y
// 'region'/'sector'/'cuadrante' para los niveles del mando unico.
// Exportada para que el click-handler priorice features mas especificas
// cuando hay overlap espacial (cuadrante dentro de sector, etc.).
export function categorizeMandoUnico(mu) {
  var v = (mu == null ? '' : String(mu)).toLowerCase()
  if (v === 'region' || v === 'sector' || v === 'cuadrante') return v
  return 'geofence'
}

export class GeofenceRenderer {
  constructor() {
    this.map = null
    this.features = new Map()
    // Default: todos visibles. MapV2 override con setFilters() al iniciar.
    this._filters = { geofence: true, region: true, sector: true, cuadrante: true }
  }

  init(map) {
    this.map = map
    if (!map.getSource(GEOFENCE_SOURCE)) {
      map.addSource(GEOFENCE_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
  }

  loadGeofences(geofences) {
    for (var i = 0; i < geofences.length; i++) {
      var gf = geofences[i]
      var geometry = wktToGeoJSON(gf.area)
      if (!geometry) continue

      // El color visual de la geocerca vive en `borderColor` para todas
      // las categorías (regular, region, sector, cuadrante). Algunas
      // geocercas regulares tienen `attrs.color: '#FFFFFF'` (blanco) que
      // es el "tinte de fill" interno del editor pero NO el color con
      // el que el operador identifica la geocerca — usar `color` como
      // fuente primaria producía fills blancos invisibles. Por eso fill
      // y borde comparten `borderColor` como fuente, con `color` como
      // fallback cuando borderColor falte.
      var attrs = gf.attributes || {}
      var primary = attrs.borderColor || attrs.color || 'blue'
      var fillColor = primary
      var borderColor = primary

      this.features.set(gf.id, {
        type: 'Feature',
        properties: {
          id: gf.id,
          name: gf.name || 'Geocerca ' + gf.id,
          fillColor: fillColor,
          borderColor: borderColor,
          mandounico: attrs.mandounico || '',
        },
        geometry: geometry,
      })
    }
    this._flush()
  }

  removeGeofences(ids) {
    for (var i = 0; i < ids.length; i++) { this.features.delete(ids[i]) }
    this._flush()
  }

  clear() {
    this.features.clear()
    this._flush()
  }

  // Carga geocercas asignadas a un device específico (botón "Ver geocercas"
  // del drawer). Las ID del feature se prefija con `device-${deviceId}-`
  // para poder limpiarlas sin tocar las geocercas globales cargadas por
  // loadGeofences.
  loadDeviceGeofences(deviceId, geofences) {
    var prefix = 'device-' + deviceId + '-'
    for (var i = 0; i < geofences.length; i++) {
      var gf = geofences[i]
      var geometry = wktToGeoJSON(gf.area)
      if (!geometry) continue
      var attrs = gf.attributes || {}
      var primary = attrs.borderColor || attrs.color || 'blue'
      this.features.set(prefix + gf.id, {
        type: 'Feature',
        properties: {
          id: prefix + gf.id,
          // originalId sin prefijo — el _flush lo usa para des-duplicar:
          // si esta misma geocerca también está en el catálogo global,
          // se oculta la versión global mientras la device-feature esté
          // activa. Sin esto, se pinta dos veces (efecto "doble borde").
          originalId: gf.id,
          name: gf.name || 'Geocerca ' + gf.id,
          fillColor: primary,
          borderColor: primary,
          mandounico: attrs.mandounico || '',
          deviceGeofence: true,
        },
        geometry: geometry,
      })
    }
    this._flush()
  }

  clearDeviceGeofences(deviceId) {
    var prefix = 'device-' + deviceId + '-'
    var toDelete = []
    this.features.forEach(function(_f, key) {
      if (typeof key === 'string' && key.indexOf(prefix) === 0) toDelete.push(key)
    })
    for (var i = 0; i < toDelete.length; i++) this.features.delete(toDelete[i])
    this._flush()
  }

  clearAllDeviceGeofences() {
    var toDelete = []
    this.features.forEach(function(_f, key) {
      if (typeof key === 'string' && key.indexOf('device-') === 0) toDelete.push(key)
    })
    for (var i = 0; i < toDelete.length; i++) this.features.delete(toDelete[i])
    this._flush()
  }

  // Actualiza los filtros por tipo y re-emite. Se llama desde MapV2 via
  // watcher del store cuando el operador prende/apaga un chip en el menu
  // de Geocercas. Las features de un device-geofence (prefix "device-")
  // no respetan filtros — viven fuera del menú global y las controla el
  // toggle del drawer.
  setFilters(filters) {
    this._filters = Object.assign(
      { geofence: true, region: true, sector: true, cuadrante: true },
      filters || {}
    )
    this._flush()
  }

  _flush() {
    if (!this.map) return
    var source = this.map.getSource(GEOFENCE_SOURCE)
    if (!source) return
    var filters = this._filters
    var visible = []
    // Pre-pase: recolectar originalId de todas las device-features activas.
    // Si una geocerca global tiene el mismo id, la ocultaremos para
    // evitar el "doble borde" cuando la geocerca del device también está
    // en el catálogo global del menú "Geocercas".
    var deviceOriginalIds = Object.create(null)
    this.features.forEach(function(f) {
      var props = f.properties
      var id = props && props.id
      if (typeof id === 'string' && id.indexOf('device-') === 0) {
        if (props.originalId != null) {
          deviceOriginalIds[String(props.originalId)] = true
        }
      }
    })
    this.features.forEach(function(f) {
      var id = f.properties && f.properties.id
      // Las device-geofences (prefix string "device-") pasan sin filtrar:
      // su visibilidad la gestiona el drawer del device, no los chips.
      if (typeof id === 'string' && id.indexOf('device-') === 0) {
        visible.push(f)
        return
      }
      // Suprimir global si ya está dibujada por la device-feature.
      if (deviceOriginalIds[String(id)]) return
      var cat = categorizeMandoUnico(f.properties && f.properties.mandounico)
      if (filters[cat] !== false) visible.push(f)
    })
    source.setData({ type: 'FeatureCollection', features: visible })
  }
}
